import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import StaffSidebar from '../components/StaffSidebar';
import Staff2FASetupRequired from './staff/Staff2FASetupRequired';
import StaffWelcomeOnboarding from './staff/StaffWelcomeOnboarding';
import DashboardPage from './staff/DashboardPageRevamped';
import LiveChatPage from './staff/LiveChatPageRevamped';
import StaffChatPage from './staff/StaffChatPageRevamped';
import StaffManagementPage from './staff/StaffManagementPageRevamped';
import UserManagementPage from './staff/UserManagementPage';
import VacanciesPage from './staff/VacanciesPage';
import NewsletterPage from './staff/NewsletterPage';
import NewsManagementPage from './staff/NewsManagementPage';
import RecruitmentHubPage from './staff/RecruitmentHubPage';
import ContentHubPage from './staff/ContentHubPage';
import BrandTeamHubPage from './staff/BrandTeamHubPage';
import PeopleHubPage from './staff/PeopleHubPage';
import TrustSafetyHubPage from './staff/TrustSafetyHubPage';
import MyProfilePage from './staff/MyProfilePage';
import AccountSettingsPage from './staff/AccountSettingsPage';
import StaffResourcesPage from './staff/StaffResourcesPage';
import LeaveManagementPage from './staff/LeaveManagementPage';
import PartnersManagementPage from './staff/PartnersManagementPage';
import PortfolioManagementPage from './staff/PortfolioManagementPage';
import PortfolioGroupsPage from './staff/PortfolioGroupsPage';
import NovaPAPage from './staff/NovaPAPage';
import StaffDirectoryPage from './staff/StaffDirectoryPage';
import WellbeingPage from './staff/WellbeingPageRevamped';
import StaffHubPage from './staff/StaffHubPageRevamped';
import ConferencePage from './staff/ConferencePage';
import TrainingPortalPage from './staff/TrainingPortalPage';
import EPortalPage from './staff/EPortalPageRevamped';
import HRInboxPage from './staff/HRInboxPage';
import ProfileAndSettingsPage from './staff/ProfileAndSettingsPage';
import SecurityManagementPage from './staff/SecurityManagementPage';
import ExecutiveProfilesPage from './staff/ExecutiveProfilesPage';
import VacancyApplicationsPage from './staff/VacancyApplicationsPage';
import DMCAManagementPage from './staff/DMCAManagementPage';
import NotificationToast from '../components/NotificationToast';
import InboxPanel, { InboxBell } from '../components/InboxPanel';
import { CallProvider, CallUI } from '../components/calling/CallProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StaffDashboardNew = () => {
  const location = useLocation();
  const getPageFromPath = () => {
    const path = location.pathname.replace('/staff/', '').replace('/', '');
    return path || 'dashboard';
  };
  
  const [activePage, setActivePage] = useState(getPageFromPath());
  const [currentUser, setCurrentUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [unreadChatsCount, setUnreadChatsCount] = useState(0);
  const [unreadStaffMessages, setUnreadStaffMessages] = useState(0);
  // Post-login gates
  const [skipped2FA, setSkipped2FA] = useState(() => sessionStorage.getItem('skipped_2fa_this_session') === '1');
  const [showWelcome, setShowWelcome] = useState(false);
  const [dismissedWelcome, setDismissedWelcome] = useState(false);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [lockdown, setLockdown] = useState(null);
  const [securityAccess, setSecurityAccess] = useState(false);
  const [dmcaAccess, setDmcaAccess] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const navigate = useNavigate();

  // Update active page when URL changes
  useEffect(() => {
    setActivePage(getPageFromPath());
  }, [location.pathname]);

  useEffect(() => {
    fetchCurrentUser();
    fetchUnreadChats();
    fetchUnreadStaffMessages();
    fetchSecurityAccess();
    // Set agent status available
    updateMyStatus();
    
    // Poll every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadChats();
      fetchUnreadStaffMessages();
    }, 30000);

    // Auto-logout after 2 hours of inactivity
    let inactivityTimer;
    const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours
    const resetInactivity = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        localStorage.removeItem('staff_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/staff/login';
      }, INACTIVITY_TIMEOUT);
    };
    ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(e => window.addEventListener(e, resetInactivity));
    resetInactivity();
    
    return () => {
      clearInterval(interval);
      clearTimeout(inactivityTimer);
      ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(e => window.removeEventListener(e, resetInactivity));
    };
  }, []);

  const updateMyStatus = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      await axios.put(`${BACKEND_URL}/api/admin/agent-status?status=available`, {}, { headers });
    } catch {}
  };

  const fetchUnreadChats = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const response = await axios.get(`${BACKEND_URL}/api/chat/sessions`, { headers });
      const activeChats = response.data.filter(s => s.status === 'active' && !s.claimed_by);
      
      // Only show notification if this is a NEW chat (count increased and we've already loaded initial data)
      if (unreadChatsCount > 0 && activeChats.length > unreadChatsCount) {
        showNotification('New Customer Chat', `New chat request waiting`);
      }
      
      setUnreadChatsCount(activeChats.length);
    } catch (error) {
      console.error('Error fetching unread chats:', error);
    }
  };

  const fetchUnreadStaffMessages = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const response = await axios.get(`${BACKEND_URL}/api/staff-chat/unread-counts`, { headers });
      const totalUnread = Object.values(response.data).reduce((sum, count) => sum + count, 0);
      
      // Only show notification if this is a NEW message (totalUnread increased and we've already loaded initial data)
      if (unreadStaffMessages > 0 && totalUnread > unreadStaffMessages) {
        showNotification('New Staff Message', `You have a new message`);
      }
      
      setUnreadStaffMessages(totalUnread);
    } catch (error) {
      console.error('Error fetching unread staff messages:', error);
    }
  };

  const showNotification = (title, message) => {
    // Play notification sound
    try {
      const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3');
      audio.volume = 0.3;
      audio.play().catch(e => console.error('Error playing sound:', e));
    } catch (error) {
      console.error('Error creating audio:', error);
    }
    
    const id = Date.now();
    setNotifications(prev => [...prev, { id, title, message }]);
    
    // Auto dismiss after 4 seconds
    setTimeout(() => {
      dismissNotification(id);
    }, 4000);
  };

  const dismissNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('staff_token');
    if (!token) {
      navigate('/staff/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchSecurityAccess = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const [sec, dmca] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/security/check-access`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/dmca/access/me`, { headers }).catch(() => ({ data: {} })),
      ]);
      setSecurityAccess(sec.data?.has_access || false);
      setDmcaAccess(dmca.data?.has_access || false);
    } catch { setSecurityAccess(false); setDmcaAccess(false); }
  };

  const fetchCurrentUser = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await axios.get(`${BACKEND_URL}/api/admin/me`, { headers });
      setCurrentUser(response.data);
      // Store for Header to read on public pages
      localStorage.setItem('admin_user', JSON.stringify(response.data));

      // Show the welcome tour on the FIRST /me for accounts that haven't completed onboarding.
      // Once dismissed or completed for this session, don't re-open it as they navigate around.
      if (!response.data?.onboarding_completed && !dismissedWelcome) {
        setShowWelcome(true);
      }
      
      // Check lockdown status
      try {
        const lockdownRes = await axios.get(`${BACKEND_URL}/api/admin/security/lockdown`, { headers });
        setLockdown(lockdownRes.data);
      } catch {}
    } catch (error) {
      console.error('Error fetching current user:', error);
      if (error.response?.status === 401) {
        navigate('/staff/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('staff_token');
    localStorage.removeItem('admin_user');
    navigate('/staff/login');
  };

  const renderPage = () => {
    const props = { getAuthHeaders, currentUser, onProfileUpdate: fetchCurrentUser, setActivePage, onLogout: handleLogout };

    switch (activePage) {
      case 'dashboard':
        return <DashboardPage {...props} />;
      case 'live-chat':
        return <LiveChatPage {...props} />;
      case 'staff-chat':
        return <StaffChatPage {...props} />;
      case 'conference':
        return <ConferencePage {...props} />;
      case 'staff-management':
        return <StaffManagementPage {...props} />;
      case 'user-management':
        return <UserManagementPage {...props} />;
      case 'vacancies':
        return <VacanciesPage {...props} />;
      case 'vacancy-applications':
        return <VacancyApplicationsPage {...props} />;
      case 'recruitment-hub':
        return <RecruitmentHubPage {...props} />;
      case 'content-hub':
        return <ContentHubPage {...props} />;
      case 'brand-team-hub':
        return <BrandTeamHubPage {...props} />;
      case 'portfolio-groups':
        return <PortfolioGroupsPage {...props} />;
      case 'people-hub':
        return <PeopleHubPage {...props} />;
      case 'trust-safety-hub':
        return <TrustSafetyHubPage {...props} securityAccess={securityAccess} dmcaAccess={dmcaAccess} />;
      case 'newsletter':
        return <NewsletterPage {...props} />;
      case 'news-management':
        return <NewsManagementPage {...props} />;
      case 'staff-resources':
        return <StaffResourcesPage {...props} />;
      case 'leave-management':
        return <LeaveManagementPage {...props} />;
      case 'partners-management':
        return <PartnersManagementPage {...props} />;
      case 'portfolio-management':
        return <PortfolioManagementPage {...props} />;
      case 'nova-pa':
        return <NovaPAPage {...props} />;
      case 'staff-directory':
        return <StaffDirectoryPage {...props} />;
      case 'wellbeing':
        return <WellbeingPage {...props} />;
      case 'staff-hub':
        return <StaffHubPage {...props} />;
      case 'training':
        return <TrainingPortalPage {...props} />;
      case 'eportal':
        return <EPortalPage {...props} />;
      case 'hr-inbox':
        return <HRInboxPage {...props} />;
      case 'my-profile':
        return <ProfileAndSettingsPage {...props} />;
      case 'account-settings':
        return <ProfileAndSettingsPage {...props} />;
      case 'security-management':
        return <SecurityManagementPage {...props} />;
      case 'executive-profiles':
        return <ExecutiveProfilesPage {...props} />;
      case 'dmca':
        return <DMCAManagementPage {...props} />;
      case 'account':
        return <ProfileAndSettingsPage {...props} />;
      default:
        return <DashboardPage {...props} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <div className="text-cyan-400 text-xl">Loading...</div>
      </div>
    );
  }

  // ═══ LOCKDOWN SCREEN ═══
  if (lockdown?.enabled && currentUser?.role !== 'superadmin') {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center p-6" data-testid="lockdown-screen">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </div>
          <h1 className="text-2xl font-medium text-white mb-3">Portal Under Maintenance</h1>
          <p className="text-white/30 text-sm leading-relaxed mb-4">{lockdown.reason || 'The staff portal is currently locked down for security purposes. Please check back later or contact your administrator.'}</p>
          <p className="text-white/15 text-xs">Activated by: {lockdown.activated_by || 'Admin'}</p>
          <button onClick={handleLogout} className="mt-8 px-6 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/50 text-sm hover:bg-white/10 transition-all">
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // ═══ 2FA REMINDER SCREEN — before dashboard, after login, unless skipped this session ═══
  const needs2FA = currentUser && currentUser.role !== 'superadmin' && !currentUser.two_factor_enabled;
  if (needs2FA && !skipped2FA) {
    return (
      <Staff2FASetupRequired
        currentUser={currentUser}
        getAuthHeaders={getAuthHeaders}
        onEnabled={() => { fetchCurrentUser(); }}
        onSkipForSession={() => { sessionStorage.setItem('skipped_2fa_this_session', '1'); setSkipped2FA(true); }}
      />
    );
  }

  // ═══ WELCOME / ONBOARDING + UK CONTRACT — first time an account signs in ═══
  // Superadmin (automation account) is always exempt from the onboarding tour.
  if (showWelcome && currentUser && currentUser.role !== 'superadmin' && !currentUser.onboarding_completed) {
    return (
      <StaffWelcomeOnboarding
        currentUser={currentUser}
        getAuthHeaders={getAuthHeaders}
        onDone={() => { setShowWelcome(false); setDismissedWelcome(true); fetchCurrentUser(); }}
        onClose={() => { setShowWelcome(false); setDismissedWelcome(true); fetchCurrentUser(); }}
      />
    );
  }

  return (
    <CallProvider currentUser={currentUser} getAuthHeaders={getAuthHeaders}>
      <div className="min-h-screen bg-[#05050A] flex">
        {/* Sidebar */}
        <StaffSidebar
          activePage={activePage}
          setActivePage={setActivePage}
          onLogout={handleLogout}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          currentUser={currentUser}
          unreadChatsCount={unreadChatsCount}
          unreadStaffMessages={unreadStaffMessages}
          isCollapsed={isSidebarCollapsed}
          setIsCollapsed={setIsSidebarCollapsed}
          securityAccess={securityAccess}
          dmcaAccess={dmcaAccess}
        />

        {/* Main Content */}
        <div className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'} p-6 lg:p-8`}>
          {renderPage()}
        </div>

        {/* Notifications */}
        <NotificationToast notifications={notifications} onDismiss={dismissNotification} />
        
        {/* Call UI Overlay */}
        <CallUI />
      </div>
    </CallProvider>
  );
};

export default StaffDashboardNew;
